import { useCallback, useEffect, useRef, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { careFileUrl } from "@/lib/care";
import { W_STATE_META, addMonths } from "@/lib/sav";
import { fmtDate, fmtDateTime } from "@/lib/helpers";
import { toast } from "sonner";
import * as Icons from "lucide-react";

const KINDS = { fabricant: "Fabricant", pose: "Pose", autre: "Autre" };
const empty = { kind: "fabricant", kind_other: "", start_date: "", end_date: "", duration_months: "", provider: "", conditions: "", client_visible: true, change_note: "" };

function WarrantyForm({ open, onOpenChange, initial, onSave }) {
  const [f, setF] = useState(empty);
  useEffect(() => { if (open) setF(initial ? { ...empty, ...initial, duration_months: initial.duration_months || "", change_note: "" } : empty); }, [open, initial]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const calc = () => { const end = addMonths(f.start_date, f.duration_months); if (end) setF({ ...f, end_date: end }); else toast.error("Renseignez le début et la durée"); };
  const submit = () => onSave({ ...f, start_date: f.start_date || null, end_date: f.end_date || null, duration_months: f.duration_months ? Number(f.duration_months) : null });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="warranty-dialog">
        <DialogHeader><DialogTitle>{initial ? "Modifier la garantie" : "Ajouter une garantie"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Nature *</Label>
              <Select value={f.kind} onValueChange={(v) => setF({ ...f, kind: v })}>
                <SelectTrigger data-testid="w-kind"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(KINDS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
              </Select></div>
            {f.kind === "autre" && <div className="space-y-1.5"><Label>Précisez</Label><Input value={f.kind_other} onChange={set("kind_other")} data-testid="w-kind-other" /></div>}
          </div>
          <div className="space-y-1.5"><Label>Organisme ou entreprise responsable</Label><Input value={f.provider} onChange={set("provider")} data-testid="w-provider" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Début de garantie</Label><Input type="date" value={f.start_date || ""} onChange={set("start_date")} data-testid="w-start" /></div>
            <div className="space-y-1.5"><Label>Fin de garantie</Label><Input type="date" value={f.end_date || ""} onChange={set("end_date")} data-testid="w-end" /></div>
          </div>
          <div className="flex items-end gap-2">
            <div className="flex-1 space-y-1.5"><Label>Durée en mois (aide au calcul)</Label><Input inputMode="numeric" value={f.duration_months} onChange={set("duration_months")} placeholder="Aucune durée par défaut" data-testid="w-duration" /></div>
            <Button type="button" variant="outline" onClick={calc} data-testid="w-calc">Calculer la fin</Button>
          </div>
          <p className="text-xs text-slate-400">La date de fin calculée reste modifiable. Le début n'est jamais déduit de la date de pose.</p>
          <div className="space-y-1.5"><Label>Conditions ou exclusions</Label><Textarea value={f.conditions} onChange={set("conditions")} data-testid="w-conditions" /></div>
          <label className="flex items-center gap-2 text-sm"><Checkbox checked={f.client_visible} onCheckedChange={(v) => setF({ ...f, client_visible: !!v })} data-testid="w-client-visible" />Afficher cette garantie au client</label>
          {initial && <div className="space-y-1.5"><Label>Motif de la modification (historique)</Label><Input value={f.change_note} onChange={set("change_note")} placeholder="Ex. nouvelles conditions après remplacement" data-testid="w-change-note" /></div>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button onClick={submit} className="bg-emerald-600 hover:bg-emerald-700" data-testid="w-save">Enregistrer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function WarrantyRow({ w, canManage, onEdit, onReload }) {
  const fileRef = useRef(null);
  const [pub, setPub] = useState(false);
  const [showHist, setShowHist] = useState(false);
  const upload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file); fd.append("client_published", pub ? "true" : "false");
    try { await api.post(`/care/warranties/${w.id}/document`, fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Justificatif ajouté"); onReload(); }
    catch (err) { toast.error(apiError(err)); } finally { e.target.value = ""; }
  };
  const togglePub = async (d) => { try { await api.post(`/care/files/${d.id}/client-publish`, { published: !d.client_published }); onReload(); } catch (e) { toast.error(apiError(e)); } };
  const archive = async () => { try { await api.post(`/care/warranties/${w.id}/archive`); toast.success("Garantie archivée (historique conservé)"); onReload(); } catch (e) { toast.error(apiError(e)); } };
  return (
    <li className="rounded-lg border border-slate-200 p-3" data-testid={`warranty-${w.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="text-sm font-semibold text-slate-800">Garantie {w.kind_label}{w.provider ? ` — ${w.provider}` : ""}</p>
          <p className="text-xs text-slate-500">{w.start_date ? `Du ${fmtDate(w.start_date)}` : "Début non renseigné"} · {w.end_date ? `au ${fmtDate(w.end_date)}` : "fin non renseignée"}{w.duration_months ? ` · ${w.duration_months} mois` : ""}</p>
          {w.conditions && <p className="mt-1 text-xs text-slate-600">{w.conditions}</p>}
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          <span className={`rounded-full border px-2 py-0.5 text-[11px] font-medium ${W_STATE_META[w.state]}`} data-testid={`warranty-state-${w.id}`}>{w.state_label}</span>
          {!w.client_visible && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-500">Masquée au client</span>}
        </div>
      </div>
      {(w.documents || []).length > 0 && (
        <ul className="mt-2 space-y-1">{w.documents.map((d) => (
          <li key={d.id} className="flex items-center justify-between gap-2 text-xs">
            <a href={careFileUrl(d.id)} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-emerald-700 hover:underline"><Icons.Paperclip className="h-3.5 w-3.5" />{d.original_filename}</a>
            {canManage && <button onClick={() => togglePub(d)} className="text-slate-500 hover:underline" data-testid={`wdoc-pub-${d.id}`}>{d.client_published ? "Publié au client — retirer" : "Non publié — publier"}</button>}
          </li>))}</ul>
      )}
      {canManage && (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => onEdit(w)} data-testid={`warranty-edit-${w.id}`}><Icons.Pencil className="mr-1 h-3.5 w-3.5" />Modifier</Button>
          <input ref={fileRef} type="file" accept="image/*,application/pdf" hidden onChange={upload} />
          <label className="flex items-center gap-1 text-xs text-slate-500"><Checkbox checked={pub} onCheckedChange={(v) => setPub(!!v)} />publier au client</label>
          <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} data-testid={`warranty-doc-${w.id}`}><Icons.Upload className="mr-1 h-3.5 w-3.5" />Justificatif</Button>
          <Button size="sm" variant="ghost" onClick={() => setShowHist(!showHist)} data-testid={`warranty-hist-${w.id}`}>Historique ({(w.history || []).length})</Button>
          <Button size="sm" variant="ghost" className="text-rose-600" onClick={archive} data-testid={`warranty-archive-${w.id}`}>Archiver</Button>
        </div>
      )}
      {showHist && <ul className="mt-2 space-y-1 border-t border-slate-100 pt-2 text-xs text-slate-500">{(w.history || []).map((h, i) => <li key={i}>{fmtDateTime(h.at)} · {h.by_name} · {h.action} — {h.detail}</li>)}</ul>}
    </li>
  );
}

export function WarrantySection({ equipmentId, canManage }) {
  const [items, setItems] = useState(null);
  const [edit, setEdit] = useState(null);
  const load = useCallback(() => { api.get(`/care/equipment/${equipmentId}/warranties`).then((r) => setItems(r.data)).catch(() => setItems([])); }, [equipmentId]);
  useEffect(() => { load(); }, [load]);
  const save = async (payload) => {
    try {
      if (edit?.id) await api.put(`/care/warranties/${edit.id}`, payload); else await api.post(`/care/equipment/${equipmentId}/warranties`, payload);
      toast.success("Garantie enregistrée"); setEdit(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Card className="p-4" data-testid="warranty-section">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="font-display text-base font-semibold text-[#0D2E24]">Garanties</h2>
        {canManage && <Button size="sm" variant="outline" onClick={() => setEdit({})} data-testid="warranty-add"><Icons.Plus className="mr-1 h-4 w-4" />Ajouter une garantie</Button>}
      </div>
      {items === null ? <p className="text-sm text-slate-400">Chargement…</p>
        : items.length === 0 ? <p className="text-sm text-slate-400" data-testid="warranty-empty">Garantie non renseignée.</p>
        : <ul className="space-y-2">{items.map((w) => <WarrantyRow key={w.id} w={w} canManage={canManage} onEdit={setEdit} onReload={load} />)}</ul>}
      <p className="mt-2 text-xs text-slate-400">Une réparation ou un remplacement ne prolonge pas automatiquement une garantie.</p>
      <WarrantyForm open={!!edit} onOpenChange={(o) => !o && setEdit(null)} initial={edit?.id ? edit : null} onSave={save} />
    </Card>
  );
}
